import { EnEventStatus } from '../dto';

/**
 * Minimal shape needed to tell whether an event is cancelled — the fields shared by
 * `SessionEventDto` (full) and `CalendarEventDto` (lean). Keeping the helpers structural
 * lets both the operator-wide calendar slice and the member views reuse them.
 */
type DeletableEvent = { deleted: string | null; status: EnEventStatus };

/**
 * Whether an event has been cancelled.
 *
 * Cancellation is a SOFT DELETE: the backend stamps `deleted` and leaves `status`
 * alone — a seduta cancelled while SCHEDULED keeps that status. Looking at the status
 * alone therefore misses it entirely, which is why cancelled sedute used to render on
 * the calendars exactly like live ones (and still blocked their slot). `DELETED` is
 * accepted as a status too, for events the backend flags that way instead.
 */
export function isEventDeleted(event: DeletableEvent): boolean {
  return event.deleted != null || event.status === EnEventStatus.DELETED;
}

/** The events still in play — cancelled ones dropped. Preserves the concrete item type. */
export function excludeDeletedEvents<T extends DeletableEvent>(events: T[]): T[] {
  return events.filter((event) => !isEventDeleted(event));
}

/**
 * The statuses a seduta can still be acted on from: not yet resolved.
 *
 * Mirrors the backend's `PENDING_EVENT_STATUSES`. `DELAYED` belongs here —
 * it means "late but still recoverable", and it is exactly the seduta an
 * operator wants to move to tomorrow. Everything else is history.
 *
 * NOTE: pending is not the same as upcoming. The cron writes `DELAYED` only once
 * the seduta's own end is past, so a DELAYED one is always in the PAST — never
 * put this list behind a "next session" filter.
 */
export const PENDING_EVENT_STATUSES: readonly EnEventStatus[] = [
  EnEventStatus.SCHEDULED,
  EnEventStatus.DELAYED,
];

/**
 * Whether this row has been superseded by another one.
 *
 * Moving a seduta does not edit it in place: the backend creates a NEW event and
 * marks the old one `RESCHEDULED`. That old row is a headstone — it carries no
 * outcome, because its replacement carries the real one, and the backend counts
 * it as zero everywhere (it is in both `RESOLVED_STATUSES` and
 * `NEUTRAL_STATUSES`, and `getDeleteReferences` puts it in no bucket).
 *
 * So it must be zero here too: counted, it would charge the same seduta twice.
 */
export function isEventSuperseded(event: DeletableEvent): boolean {
  return event.status === EnEventStatus.RESCHEDULED;
}

/**
 * The events that actually stand for something: neither cancelled nor superseded.
 * This is the set every count, KPI and gate should be built on.
 */
export function activeEvents<T extends DeletableEvent>(events: T[]): T[] {
  return events.filter((event) => !isEventDeleted(event) && !isEventSuperseded(event));
}
